import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  AlertTriangle,
  Briefcase,
  Cog,
  Loader2,
  Save,
  Sparkles,
  User
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import TabsComponent from "@/components/tabs/tabs-component";
import type { TabItemProps } from "@/components/tabs/tab-component.type";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import {
  useFilterBuilder,
  useSortBuilder,
  AttributeDataType,
  type FilterableAttribute,
  type SortableAttribute,
  serializeFiltersToQuery,
  serializeSortsToQuery
} from "@/components/search-filter-bar";
import { useDebounce } from "@/hooks/use-debounce";
import { useScheduleCalendar } from "@/hooks/use-schedule-calendar";
import {
  useGetData,
  useInvalidateQueries,
  usePostData,
} from "@/hooks/services/use-query-hooks";
import {
  AskAiJobSchedulePlanning,
  GetJobScheduleByMachine,
  GetJobScheduleByTechnician,
  GetJobSchedulePlanningByJob
} from "@/hooks/services/pps/handlers/job-schedule-api";
import { UpdateJobOrderWorkflowDateBatch } from "@/hooks/services/pps/handlers/job-order-workflow";
import { MachineListGroupedByShop } from "@/hooks/services/pps/handlers/machines-api";
import { TechnicianList } from "@/hooks/services/pps/handlers/technicians-api";
import {
  JobPlanningInfeasibleItem,
  RecommendationMachineSchedule,
  RecommendationTechnicianSchedule,
  JobPlanningRecommendationItem,
  JobScheduleItem,
  MachineScheduleItem,
  TechnicianScheduleItem
} from "@/hooks/services/pps/types/job-schedule.type";
import type { MachineGroupedItem } from "@/hooks/services/pps/types/machines.type";
import type { Technician } from "@/hooks/services/pps/types/technicians.type";
import type {
  JobAssignment,
  MachineScheduleGantt,
  ScheduledJob,
  TechnicianScheduleGantt,
  ViewMode
} from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import { toastManager } from "@/lib/toast-manager";

import JobGanttChart from "./job-schedule";
import MachineGanttChart from "./machine-schedule";
import TechnicianGanttChart from "./technician-schedule";
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "../utils";

interface AiPlanningDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId?: number;
  filterableAttributes: FilterableAttribute[];
  sortableAttributes: SortableAttribute[];
}

const transformJobScheduleToAssignment = (
  item: JobScheduleItem,
  index: number
): JobAssignment => ({
  id: `plan-assign-${item.jobOrderId}-${index}`,
  assignmentId: `plan-${item.jobOrderId}-${index}`,
  jobOrderNo: item.jobOrderNo,
  jobOrderId: item.jobOrderId,
  processId: 0,
  processCode: 0,
  processDesc: item.processName || "",
  partNo: item.partNumber || "",
  partDescription: item.partDescription || "",
  quantity: 0,
  standardHours: item.durationHours ?? 0,
  startDate: item.startDate || "",
  eddDate: item.eddDate || "",
  duration: item.durationHours ?? 0,
  technicianId: item.technicianId ?? null,
  technicianName: item.technicianName,
  machineId: item.machineId ?? null,
  machineName: item.machineName,
  statusId: 0,
  status: item.status || "scheduled",
  priority: item.priority ?? 1,
  notes: null,
  customerName: item.customerName || null
});

const transformToScheduledJobs = (items: JobScheduleItem[]): ScheduledJob[] => {
  const jobsMap = new Map<number, ScheduledJob>();
  const insertionOrder: number[] = [];

  items.forEach((item, index) => {
    if (!jobsMap.has(item.jobOrderId)) {
      insertionOrder.push(item.jobOrderId);
      jobsMap.set(item.jobOrderId, {
        id: item.jobOrderId,
        jobOrderNo: item.jobOrderNo,
        jobOrderId: item.jobOrderId,
        partNo: item.partNumber || "",
        partDescription: item.partDescription || "",
        customerName: item.customerName || "",
        totalQuantity: 0,
        overallStartDate: item.startDate || "",
        overallEddDate: item.eddDate || "",
        assignments: []
      });
    }

    const job = jobsMap.get(item.jobOrderId)!;
    job.assignments.push(transformJobScheduleToAssignment(item, index));

    if (!job.partNo && item.partNumber) {
      job.partNo = item.partNumber;
    }

    if (!job.partDescription && item.partDescription) {
      job.partDescription = item.partDescription;
    }

    if (!job.customerName && item.customerName) {
      job.customerName = item.customerName;
    }

    const currentStart = item.startDate || "";
    const currentEnd = item.eddDate || "";

    if (
      currentStart &&
      (!job.overallStartDate || currentStart < job.overallStartDate)
    ) {
      job.overallStartDate = currentStart;
    }

    if (
      currentEnd &&
      (!job.overallEddDate || currentEnd > job.overallEddDate)
    ) {
      job.overallEddDate = currentEnd;
    }
  });

  return insertionOrder.map((jobOrderId) => jobsMap.get(jobOrderId)!);
};

const toDateOnly = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  return value.includes("T") ? value.slice(0, 10) : value;
};

const hasDateTimeValue = (value?: string | null): boolean =>
  !!value && String(value).trim().length > 0;

const pickValue = <T,>(
  source: any,
  camelKey: string,
  snakeKey: string
): T | undefined => source?.[camelKey] ?? source?.[snakeKey];

const toNumberOrZero = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseIsoDateTimeOrNull = (value: any): string | null => {
  if (!value) {
    return null;
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }

  // Preserve backend timestamp values as-is to avoid local timezone shifts in UI.
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${text}T00:00:00`;
  }

  return text.includes(" ") ? text.replace(" ", "T") : text;
};

const getStandardHoursForFallback = (row: JobScheduleItem): number => {
  const source = row as any;
  const processStandardHour = toNumberOrZero(
    source?.processStandardHour ??
      source?.processStandardHours ??
      source?.ProcessStandardHour ??
      source?.ProcessStandardHours
  );
  const labourStandardHour = toNumberOrZero(
    source?.labourStandardHour ??
      source?.labourStandardHours ??
      source?.LaborStandardHour ??
      source?.LabourStandardHour ??
      source?.LabourStandardHours
  );

  return Math.max(processStandardHour, labourStandardHour);
};

const withFallbackEddDate = (row: JobScheduleItem): JobScheduleItem => {
  if (hasDateTimeValue(row.eddDate) || !hasDateTimeValue(row.startDate)) {
    return row;
  }

  const standardHours = getStandardHoursForFallback(row);
  if (standardHours <= 0) {
    return row;
  }

  const start = dayjs(row.startDate);
  if (!start.isValid()) {
    return row;
  }

  return {
    ...row,
    eddDate: start.add(standardHours, "hour").format("YYYY-MM-DDTHH:mm:ss"),
    durationHours: row.durationHours ?? standardHours
  };
};

const estimateDurationHours = (
  startDateTime?: string | null,
  endDateTime?: string | null,
  fallbackSlots = 1
): number => {
  if (startDateTime && endDateTime) {
    const start = new Date(startDateTime).getTime();
    const end = new Date(endDateTime).getTime();
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
      const diffHours = Math.ceil((end - start) / (1000 * 60 * 60));
      return Math.max(1, diffHours);
    }
  }

  return Math.max(1, fallbackSlots) * 4;
};

const normalizeMachineSchedule = (
  value: any
): RecommendationMachineSchedule | null => {
  if (!value) {
    return null;
  }

  const machineId = toNumberOrZero(
    pickValue<number>(value, "machineId", "machine_id")
  );
  if (!machineId) {
    return null;
  }

  return {
    machineId,
    startDatetime: parseIsoDateTimeOrNull(
      pickValue<string>(value, "startDatetime", "start_datetime") ??
        value?.startDateTime
    ),
    endDatetime: parseIsoDateTimeOrNull(
      pickValue<string>(value, "endDatetime", "end_datetime") ??
        value?.endDateTime
    ),
    durationSlots: toNumberOrZero(
      pickValue<number>(value, "durationSlots", "duration_slots")
    )
  };
};

const normalizeTechnicianSchedule = (
  value: any
): RecommendationTechnicianSchedule | null => {
  if (!value) {
    return null;
  }

  const technicianId = toNumberOrZero(
    pickValue<number>(value, "technicianId", "technician_id")
  );
  if (!technicianId) {
    return null;
  }

  return {
    technicianId,
    startDatetime: parseIsoDateTimeOrNull(
      pickValue<string>(value, "startDatetime", "start_datetime") ??
        value?.startDateTime
    ),
    endDatetime: parseIsoDateTimeOrNull(
      pickValue<string>(value, "endDatetime", "end_datetime") ??
        value?.endDateTime
    ),
    durationSlots: toNumberOrZero(
      pickValue<number>(value, "durationSlots", "duration_slots")
    )
  };
};

const normalizeRecommendation = (
  item: any
): JobPlanningRecommendationItem | null => {
  const workflowId = toNumberOrZero(
    pickValue<number>(item, "workflowId", "workflow_id")
  );
  const jobOrderId = toNumberOrZero(
    pickValue<number>(item, "jobOrderId", "job_order_id")
  );
  const recommendedMachineId = toNumberOrZero(
    pickValue<number>(item, "recommendedMachineId", "recommended_machine_id")
  );
  const recommendedTechnicianId = toNumberOrZero(
    pickValue<number>(
      item,
      "recommendedTechnicianId",
      "recommended_technician_id"
    )
  );

  if (
    !workflowId ||
    !jobOrderId ||
    !recommendedMachineId ||
    !recommendedTechnicianId
  ) {
    return null;
  }

  const machineSchedule = normalizeMachineSchedule(
    pickValue<any>(item, "machineSchedule", "machine_schedule")
  );
  const technicianSchedule = normalizeTechnicianSchedule(
    pickValue<any>(item, "technicianSchedule", "technician_schedule")
  );

  const baseStartDateTime = parseIsoDateTimeOrNull(
    pickValue<string>(
      item,
      "recommendedStartDatetime",
      "recommended_start_datetime"
    ) ?? item?.recommendedStartDateTime
  );
  const baseEndDateTime = parseIsoDateTimeOrNull(
    pickValue<string>(
      item,
      "recommendedEndDatetime",
      "recommended_end_datetime"
    ) ?? item?.recommendedEndDateTime
  );

  const fallbackStartDate = pickValue<string>(
    item,
    "recommendedStartDate",
    "recommended_start_date"
  );
  const resolvedStartDate =
    toDateOnly(baseStartDateTime) || toDateOnly(fallbackStartDate) || "";

  return {
    workflowId,
    jobOrderId,
    jobOrderNo: pickValue<string>(item, "jobOrderNo", "job_order_no") || null,
    taskId: pickValue<number>(item, "taskId", "task_id") ?? null,
    taskNo: pickValue<string>(item, "taskNo", "task_no") ?? null,
    processName: pickValue<string>(item, "processName", "process_name") ?? null,
    partNumber: pickValue<string>(item, "partNumber", "part_number") ?? null,
    partDescription:
      pickValue<string>(item, "partDescription", "part_description") ?? null,
    customerName:
      pickValue<string>(item, "customerName", "customer_name") ?? null,
    recommendedStartDatetime:
      baseStartDateTime ||
      machineSchedule?.startDatetime ||
      technicianSchedule?.startDatetime,
    recommendedEndDatetime:
      baseEndDateTime ||
      machineSchedule?.endDatetime ||
      technicianSchedule?.endDatetime,
    recommendedStartDate: resolvedStartDate,
    durationSlots: toNumberOrZero(
      pickValue<number>(item, "durationSlots", "duration_slots")
    ),
    recommendedMachineId,
    recommendedMachineName:
      pickValue<string>(
        item,
        "recommendedMachineName",
        "recommended_machine_name"
      ) || null,
    recommendedTechnicianId,
    recommendedTechnicianName:
      pickValue<string>(
        item,
        "recommendedTechnicianName",
        "recommended_technician_name"
      ) || null,
    machineSchedule,
    technicianSchedule
  };
};

const buildResourceKey = (
  id: number | string | null | undefined,
  name?: string | null
): string => {
  if (id !== null && id !== undefined && id !== "") {
    return `id:${String(id)}`;
  }

  const normalizedName = (name || "").trim().toLowerCase();
  return `name:${normalizedName}`;
};

const hasResourceIdentity = (
  id: number | string | null | undefined,
  name?: string | null
): boolean => {
  if (id !== null && id !== undefined && id !== "") {
    return true;
  }

  return (name || "").trim().length > 0;
};

const normalizeProcessIdentity = (value?: string | null): string =>
  (value || "").trim().toLocaleLowerCase();

const recommendationMatchesResourceApiRow = (
  recommendation: JobPlanningRecommendationItem,
  row: { jobOrderId: number; processName?: string | null },
  processName: string | null | undefined
): boolean =>
  recommendation.jobOrderId === row.jobOrderId &&
  !!normalizeProcessIdentity(processName) &&
  normalizeProcessIdentity(processName) ===
    normalizeProcessIdentity(recommendation.processName || recommendation.taskNo);

const buildAssignmentKey = (assignment: JobAssignment): string =>
  [assignment.jobOrderId, (assignment.processDesc || "").trim().toLowerCase()].join("|");

const pushUniqueAssignments = (
  target: JobAssignment[],
  incoming: JobAssignment[]
) => {
  const existingKeys = new Set(target.map(buildAssignmentKey));

  incoming.forEach((assignment) => {
    const key = buildAssignmentKey(assignment);
    if (!existingKeys.has(key)) {
      existingKeys.add(key);
      target.push(assignment);
    }
  });
};

const transformRecommendationToSchedule = (
  item: JobPlanningRecommendationItem
): JobScheduleItem => {
  const startDate =
    item.recommendedStartDatetime || `${item.recommendedStartDate}T00:00:00`;
  const endDate = item.recommendedEndDatetime || startDate;
  const durationHours = estimateDurationHours(
    item.recommendedStartDatetime,
    item.recommendedEndDatetime,
    item.durationSlots || 1
  );

  return {
    workflowId: item.workflowId,
    jobOrderId: item.jobOrderId,
    jobOrderNo: item.jobOrderNo || "",
    partNumber: item.partNumber || null,
    partDescription: item.partDescription || null,
    customerName: item.customerName || null,
    processName: item.processName || item.taskNo || null,
    technicianId: item.recommendedTechnicianId,
    technicianName:
      item.recommendedTechnicianName || `Tech #${item.recommendedTechnicianId}`,
    machineId: item.recommendedMachineId,
    machineName:
      item.recommendedMachineName || `Machine #${item.recommendedMachineId}`,
    startDate,
    eddDate: endDate,
    durationHours,
    priority: 1,
    status: "AI Planned",
    isUnscheduled: false
  };
};

const transformToTechnicianSchedule = (
  items: TechnicianScheduleItem[]
): TechnicianScheduleGantt[] => {
  const techMap = new Map<number, TechnicianScheduleGantt>();
  const insertionOrder: number[] = [];

  items.forEach((item, index) => {
    if (!techMap.has(item.technicianId)) {
      insertionOrder.push(item.technicianId);
      techMap.set(item.technicianId, {
        shopName: item.shopName || "",
        technicianId: item.technicianId,
        technicianName: item.technicianName,
        assignedJobs: []
      });
    }

    techMap.get(item.technicianId)!.assignedJobs.push({
      id: `tech-${item.technicianId}-${item.jobOrderId}-${index}`,
      assignmentId: `${item.technicianId}-${item.jobOrderId}-${index}`,
      jobOrderNo: item.jobOrderNo,
      jobOrderId: item.jobOrderId,
      processId: 0,
      processCode: 0,
      processDesc: item.processName || "",
      partNo: item.partNumber || "",
      partDescription: item.partDescription || "",
      quantity: 0,
      standardHours: item.durationHours ?? 0,
      startDate: (item as any).startDate || item.startTime || "",
      eddDate: (item as any).eddDate || item.endTime || "",
      duration: item.durationHours ?? 0,
      technicianId: item.technicianId,
      technicianName: item.technicianName,
      machineId: null,
      machineName: item.machineName,
      statusId: 0,
      status: item.status || "scheduled",
      priority: item.priority ?? 1,
      notes: null
    });
  });

  return insertionOrder.map((technicianId) => techMap.get(technicianId)!);
};

const transformToMachineSchedule = (
  items: MachineScheduleItem[]
): MachineScheduleGantt[] => {
  const machineMap = new Map<number, MachineScheduleGantt>();
  const insertionOrder: number[] = [];

  const getMachineStatus = (
    status: number
  ): "available" | "maintenance" | "unavailable" => {
    switch (status) {
      case 2:
        return "maintenance";
      case 3:
        return "unavailable";
      default:
        return "available";
    }
  };

  items.forEach((item, index) => {
    if (!machineMap.has(item.machineId)) {
      insertionOrder.push(item.machineId);
      machineMap.set(item.machineId, {
        machineId: item.machineId,
        machineName: item.machineName,
        shopName: item.shopName || "",
        status: getMachineStatus(item.machineStatus),
        assignedJobs: []
      });
    }

    machineMap.get(item.machineId)!.assignedJobs.push({
      id: `machine-${item.machineId}-${item.jobOrderId}-${index}`,
      assignmentId: `${item.machineId}-${item.jobOrderId}-${index}`,
      jobOrderNo: item.jobOrderNo,
      jobOrderId: item.jobOrderId,
      processId: 0,
      processCode: 0,
      processDesc: item.processName || "",
      partNo: item.partNumber || "",
      partDescription: item.partDescription || "",
      quantity: 0,
      standardHours: item.durationHours ?? 0,
      startDate: item.startDate || item.startTime || "",
      eddDate: item.eddDate || item.endTime || "",
      duration: item.durationHours ?? 0,
      technicianId: null,
      technicianName: item.technicianName,
      machineId: item.machineId,
      machineName: item.machineName,
      statusId: 0,
      status: item.status || "scheduled",
      priority: item.priority ?? 1,
      notes: null
    });
  });

  return insertionOrder.map((machineId) => machineMap.get(machineId)!);
};

const transformJobRowsToTechnicianSchedule = (
  items: JobScheduleItem[],
  recommendations: JobPlanningRecommendationItem[] = []
): TechnicianScheduleGantt[] => {
  const map = new Map<string, TechnicianScheduleGantt>();
  const recommendationsByWorkflowId = new Map(
    recommendations.map((item) => [item.workflowId, item])
  );

  items.forEach((item, index) => {
    if (!hasResourceIdentity(item.technicianId, item.technicianName)) {
      return;
    }

    const recommendation = item.workflowId
      ? recommendationsByWorkflowId.get(item.workflowId)
      : undefined;
    const resourceSchedule = recommendation?.technicianSchedule;
    const assignmentSource = resourceSchedule
      ? {
          ...item,
          startDate: resourceSchedule.startDatetime || item.startDate,
          eddDate: resourceSchedule.endDatetime || item.eddDate,
          durationHours: resourceSchedule.durationSlots || item.durationHours
        }
      : item;

    const key = buildResourceKey(item.technicianId, item.technicianName);
    if (!map.has(key)) {
      map.set(key, {
        technicianId: item.technicianId ?? 0,
        technicianName: item.technicianName || "Unassigned",
        shopName: "",
        assignedJobs: []
      });
    }

    map
      .get(key)!
      .assignedJobs.push(transformJobScheduleToAssignment(assignmentSource, index));
  });

  return Array.from(map.values());
};

const transformJobRowsToMachineSchedule = (
  items: JobScheduleItem[],
  recommendations: JobPlanningRecommendationItem[] = []
): MachineScheduleGantt[] => {
  const map = new Map<string, MachineScheduleGantt>();
  const recommendationsByWorkflowId = new Map(
    recommendations.map((item) => [item.workflowId, item])
  );

  items.forEach((item, index) => {
    if (!hasResourceIdentity(item.machineId, item.machineName)) {
      return;
    }

    const recommendation = item.workflowId
      ? recommendationsByWorkflowId.get(item.workflowId)
      : undefined;
    const resourceSchedule = recommendation?.machineSchedule;
    const assignmentSource = resourceSchedule
      ? {
          ...item,
          startDate: resourceSchedule.startDatetime || item.startDate,
          eddDate: resourceSchedule.endDatetime || item.eddDate,
          durationHours: resourceSchedule.durationSlots || item.durationHours
        }
      : item;

    const key = buildResourceKey(item.machineId, item.machineName);
    if (!map.has(key)) {
      map.set(key, {
        machineId: item.machineId ?? 0,
        machineName: item.machineName || "No machine",
        shopName: "",
        status: "available",
        assignedJobs: []
      });
    }

    map
      .get(key)!
      .assignedJobs.push(transformJobScheduleToAssignment(assignmentSource, index));
  });

  return Array.from(map.values());
};

export default function AiPlanningDrawer({
  open,
  onOpenChange,
  workspaceId,
  filterableAttributes,
  sortableAttributes
}: AiPlanningDrawerProps) {
  const [search, setSearch] = useState("");
  const [activeCalendarTab, setActiveCalendarTab] = useState("job");
  const showTechnicianWithJobsOnly = true;
  const showMachineWithJobsOnly = true;
  const [viewMode, setViewMode] = useState<ViewMode>("weekly");
  const [zoomLevel, setZoomLevel] = useState<number>(30);
  const onZoomIn = () => setZoomLevel((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  const onZoomOut = () =>
    setZoomLevel((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
  const { dateRange, setDateRange } = useScheduleCalendar("weekly");
  useEffect(() => {
    if (activeCalendarTab === "job") {
      setZoomLevel(20);
    } else {
      setZoomLevel(30);
    }
  }, [activeCalendarTab]);
  const [aiRecommendations, setAiRecommendations] = useState<
    JobPlanningRecommendationItem[]
  >([]);
  const [aiInfeasible, setAiInfeasible] = useState<JobPlanningInfeasibleItem[]>(
    []
  );

  const debouncedSearch = useDebounce(search, 500);

  const { filters, addFilter, updateFilter, removeFilter, clearFilters } =
    useFilterBuilder();
  const { sorts, addSort, updateSort, removeSort, clearSorts } =
    useSortBuilder();

  const debouncedFilters = useDebounce(filters, 500);
  const debouncedSorts = useDebounce(sorts, 500);

  const planningQueryParams = useMemo(
    () => ({
      workspaceId,
      search: debouncedSearch || undefined,
      filters:
        debouncedFilters.length > 0
          ? serializeFiltersToQuery(debouncedFilters)
          : undefined,
      sorts:
        debouncedSorts.length > 0
          ? serializeSortsToQuery(debouncedSorts)
          : undefined
    }),
    [workspaceId, debouncedSearch, debouncedFilters, debouncedSorts]
  );

  const {
    data: planningData,
    isLoading,
    refetch: refetchPlanning
  } = useGetData(GetJobSchedulePlanningByJob(planningQueryParams), {
    enabled: open && !!workspaceId
  });

  const scheduleQueryParams = useMemo(
    () => ({
      workspaceId,
      search: planningQueryParams.search,
      filters: planningQueryParams.filters,
      sorts: planningQueryParams.sorts,
      from: dateRange.from
        ? dayjs(dateRange.from).format("YYYY-MM-DD")
        : undefined,
      to: dateRange.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : undefined
    }),
    [workspaceId, planningQueryParams, dateRange]
  );

  const {
    data: technicianData,
    isLoading: isLoadingTechnicians,
    refetch: refetchTechnician
  } = useGetData(GetJobScheduleByTechnician(scheduleQueryParams), {
    enabled: open && !!workspaceId
  });

  const {
    data: technicianListData,
    isLoading: isLoadingTechnicianList,
    refetch: refetchTechnicianList
  } = useGetData(
    TechnicianList({
      page: 1,
      pageSize: 100,
      workspaceId: workspaceId || 0,
      search: debouncedSearch || undefined
    }),
    {
      enabled: open && !!workspaceId
    }
  );

  const {
    data: machineData,
    isLoading: isLoadingMachines,
    refetch: refetchMachine
  } = useGetData(GetJobScheduleByMachine(scheduleQueryParams), {
    enabled: open && !!workspaceId
  });

  const {
    data: machineMasterData,
    isLoading: isLoadingMachineList,
    refetch: refetchMachineList
  } = useGetData(
    MachineListGroupedByShop({
      page: 1,
      pageSize: 100,
      workspaceId: workspaceId || 0,
      search: debouncedSearch || undefined
    }),
    {
      enabled: open && !!workspaceId
    }
  );

  const askAiPlanning = usePostData(AskAiJobSchedulePlanning());
  const saveAiPlanning = usePostData(UpdateJobOrderWorkflowDateBatch());
  const invalidateQueries = useInvalidateQueries();
  const [aiPlanningProgress, setAiPlanningProgress] = useState(0);

  useEffect(() => {
    if (!askAiPlanning.isPending) {
      setAiPlanningProgress(0);
      return;
    }

    setAiPlanningProgress(0);
    const estimatedDurationMs = 30_000;
    const startedAt = Date.now();

    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min(
        95,
        Math.floor((elapsed / estimatedDurationMs) * 95)
      );
      setAiPlanningProgress(progress);
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [askAiPlanning.isPending]);

  const planningRows = useMemo(
    () => planningData?.schedules ?? [],
    [planningData?.schedules]
  );

  const rowsWithoutEddDate = useMemo(
    () => planningRows.filter((row) => !hasDateTimeValue(row.eddDate)),
    [planningRows]
  );

  const displayPlanningRows = useMemo(
    () => planningRows.map((row) => withFallbackEddDate(row)),
    [planningRows]
  );

  const plannedWorkflowIds = useMemo(
    () => new Set(aiRecommendations.map((item) => item.workflowId)),
    [aiRecommendations]
  );

  const infeasibleWorkflowIds = useMemo(
    () => new Set(aiInfeasible.map((item) => item.workflowId)),
    [aiInfeasible]
  );

  const pendingUnscheduledRows = useMemo(
    () =>
      rowsWithoutEddDate.filter((row) => {
        const workflowId = row.workflowId;
        if (workflowId) {
          return (
            !plannedWorkflowIds.has(workflowId) &&
            !infeasibleWorkflowIds.has(workflowId)
          );
        }

        const plannedByKey = aiRecommendations.some(
          (rec) =>
            rec.jobOrderId === row.jobOrderId &&
            (rec.processName || rec.taskNo || "") === (row.processName || "")
        );

        const infeasibleByKey = aiInfeasible.some(
          (rec) =>
            rec.jobOrderId === row.jobOrderId &&
            (rec.processName || "") === (row.processName || "")
        );

        return !plannedByKey && !infeasibleByKey;
      }),
    [
      rowsWithoutEddDate,
      plannedWorkflowIds,
      infeasibleWorkflowIds,
      aiRecommendations,
      aiInfeasible
    ]
  );

  const scheduledRows = useMemo(
    () =>
      displayPlanningRows.filter(
        (row) => !(row.isUnscheduled || !hasDateTimeValue(row.startDate))
      ),
    [displayPlanningRows]
  );

  const aiRecommendedRows = useMemo(
    () => aiRecommendations.map(transformRecommendationToSchedule),
    [aiRecommendations]
  );

  const mergedScheduledRows = useMemo(() => {
    if (aiRecommendedRows.length === 0) {
      return scheduledRows;
    }

    const scheduledRowsByWorkflowId = new Map<number, JobScheduleItem>();
    const scheduledRowsByFallbackKey = new Map<string, JobScheduleItem>();

    scheduledRows.forEach((row) => {
      if (typeof row.workflowId === "number") {
        scheduledRowsByWorkflowId.set(row.workflowId, row);
      }

      const fallbackKey = `${row.jobOrderId}|${(row.processName || "").trim()}`;
      if (!scheduledRowsByFallbackKey.has(fallbackKey)) {
        scheduledRowsByFallbackKey.set(fallbackKey, row);
      }
    });

    const aiRowsWithFallbackMetadata = aiRecommendedRows.map((row) => {
      const fallbackKey = `${row.jobOrderId}|${(row.processName || "").trim()}`;
      const sourceRow =
        (typeof row.workflowId === "number"
          ? scheduledRowsByWorkflowId.get(row.workflowId)
          : undefined) || scheduledRowsByFallbackKey.get(fallbackKey);

      if (!sourceRow) {
        return row;
      }

      return {
        ...row,
        jobOrderNo: row.jobOrderNo || sourceRow.jobOrderNo,
        processName: row.processName || sourceRow.processName,
        partNumber: row.partNumber || sourceRow.partNumber,
        partDescription: row.partDescription || sourceRow.partDescription,
        customerName: row.customerName || sourceRow.customerName,
        technicianName: row.technicianName || sourceRow.technicianName,
        machineName: row.machineName || sourceRow.machineName
      };
    });

    // Replace existing scheduled rows that match AI recommendations in-memory
    // so preview/summary reflects the latest AI plan instead of merged duplicates.
    const aiWorkflowIds = new Set(
      aiRowsWithFallbackMetadata
        .map((row) => row.workflowId)
        .filter((id): id is number => typeof id === "number")
    );

    const aiFallbackKeys = new Set(
      aiRowsWithFallbackMetadata.map(
        (row) => `${row.jobOrderId}|${(row.processName || "").trim()}`
      )
    );

    const remainingScheduledRows = scheduledRows.filter((row) => {
      if (
        typeof row.workflowId === "number" &&
        aiWorkflowIds.has(row.workflowId)
      ) {
        return false;
      }

      const fallbackKey = `${row.jobOrderId}|${(row.processName || "").trim()}`;
      if (aiFallbackKeys.has(fallbackKey)) {
        return false;
      }

      return true;
    });

    const merged = [...remainingScheduledRows, ...aiRowsWithFallbackMetadata];
    const deduped: JobScheduleItem[] = [];
    const seen = new Set<string>();

    merged.forEach((row) => {
      const key = [
        row.workflowId || "",
        row.jobOrderId,
        row.processName || "",
        row.startDate || "",
        row.machineName || "",
        row.technicianName || ""
      ].join("|");

      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(row);
      }
    });

    return deduped;
  }, [scheduledRows, aiRecommendedRows]);

  const scheduledJobs = useMemo(
    () => transformToScheduledJobs(mergedScheduledRows),
    [mergedScheduledRows]
  );

  const technicianMasterRows = useMemo(
    () =>
      ((technicianListData?.data as Technician[] | undefined) ?? []).map(
        (technician) => ({
          technicianId: technician.id,
          technicianName: technician.name,
          shopName: technician.shop?.name || "",
          assignedJobs: []
        })
      ),
    [technicianListData?.data]
  );

  const machineMasterRows = useMemo(() => {
    const groupedShops = (machineMasterData?.data || []) as {
      shopName: string;
      machines: MachineGroupedItem[];
    }[];

    return groupedShops.flatMap((shop) =>
      (shop.machines || []).map((machine) => ({
        machineId: machine.id,
        machineName: machine.name,
        shopName: shop.shopName || machine.shop?.shopName || "",
        status:
          machine.status?.id === 2
            ? ("maintenance" as const)
            : machine.status?.id === 3
              ? ("unavailable" as const)
              : ("available" as const),
        assignedJobs: []
      }))
    );
  }, [machineMasterData?.data]);

  const technicianSchedules = useMemo(() => {
    const scheduleRows = transformToTechnicianSchedule(
      ((technicianData?.schedules as TechnicianScheduleItem[]) ?? []).filter(
        (row) =>
          !aiRecommendations.some(
            (recommendation) =>
              recommendationMatchesResourceApiRow(
                recommendation,
                row,
                row.processName
              ) && recommendation.recommendedTechnicianId !== row.technicianId
          )
      )
    );
    const scheduleRowsFromJobs =
      transformJobRowsToTechnicianSchedule(mergedScheduledRows, aiRecommendations);

    const map = new Map<string, TechnicianScheduleGantt>();

    technicianMasterRows.forEach((tech) => {
      const key = buildResourceKey(tech.technicianId, tech.technicianName);
      map.set(key, {
        ...tech,
        assignedJobs: [...tech.assignedJobs]
      });
    });

    [...scheduleRowsFromJobs, ...scheduleRows].forEach((tech) => {
      const key = buildResourceKey(tech.technicianId, tech.technicianName);

      if (!map.has(key)) {
        map.set(key, {
          ...tech,
          assignedJobs: [...tech.assignedJobs]
        });
        return;
      }

      const current = map.get(key)!;
      current.technicianName = current.technicianName || tech.technicianName;
      current.shopName = current.shopName || tech.shopName;
      pushUniqueAssignments(current.assignedJobs, tech.assignedJobs);
    });

    return Array.from(map.values());
  }, [
    technicianData?.schedules,
    technicianMasterRows,
    mergedScheduledRows,
    aiRecommendations
  ]);

  const machineSchedules = useMemo(() => {
    const scheduleRows = transformToMachineSchedule(
      ((machineData?.schedules as MachineScheduleItem[]) ?? []).filter(
        (row) =>
          !aiRecommendations.some(
            (recommendation) =>
              recommendationMatchesResourceApiRow(
                recommendation,
                row,
                row.processName
              ) && recommendation.recommendedMachineId !== row.machineId
          )
      )
    );
    const scheduleRowsFromJobs =
      transformJobRowsToMachineSchedule(mergedScheduledRows, aiRecommendations);

    const map = new Map<string, MachineScheduleGantt>();

    machineMasterRows.forEach((machine) => {
      const key = buildResourceKey(machine.machineId, machine.machineName);
      map.set(key, {
        ...machine,
        assignedJobs: [...machine.assignedJobs]
      });
    });

    [...scheduleRowsFromJobs, ...scheduleRows].forEach((machine) => {
      const key = buildResourceKey(machine.machineId, machine.machineName);

      if (!map.has(key)) {
        map.set(key, {
          ...machine,
          assignedJobs: [...machine.assignedJobs]
        });
        return;
      }

      const current = map.get(key)!;
      current.machineName = current.machineName || machine.machineName;
      current.shopName = current.shopName || machine.shopName;
      current.status = current.status || machine.status;
      pushUniqueAssignments(current.assignedJobs, machine.assignedJobs);
    });

    return Array.from(map.values());
  }, [
    machineData?.schedules,
    machineMasterRows,
    mergedScheduledRows,
    aiRecommendations
  ]);

  const technicianSchedulesWithJobs = useMemo(
    () => technicianSchedules.filter((item) => item.assignedJobs.length > 0),
    [technicianSchedules]
  );

  const machineSchedulesWithJobs = useMemo(
    () => machineSchedules.filter((item) => item.assignedJobs.length > 0),
    [machineSchedules]
  );

  const visibleTechnicianSchedules = useMemo(() => {
    const base = showTechnicianWithJobsOnly
      ? technicianSchedulesWithJobs
      : technicianSchedules;
    if (!debouncedSearch) return base;
    const q = debouncedSearch.toLowerCase();
    return base.filter(
      (tech) =>
        tech.technicianName?.toLowerCase().includes(q) ||
        tech.shopName?.toLowerCase().includes(q) ||
        tech.assignedJobs.some(
          (j) =>
            j.jobOrderNo?.toLowerCase().includes(q) ||
            j.processDesc?.toLowerCase().includes(q) ||
            j.machineName?.toLowerCase().includes(q)
        )
    );
  }, [
    showTechnicianWithJobsOnly,
    technicianSchedulesWithJobs,
    technicianSchedules,
    debouncedSearch
  ]);

  const visibleMachineSchedules = useMemo(() => {
    const base = showMachineWithJobsOnly ? machineSchedulesWithJobs : machineSchedules;
    if (!debouncedSearch) return base;
    const q = debouncedSearch.toLowerCase();
    return base.filter(
      (machine) =>
        machine.machineName?.toLowerCase().includes(q) ||
        machine.shopName?.toLowerCase().includes(q) ||
        machine.assignedJobs.some(
          (j) =>
            j.jobOrderNo?.toLowerCase().includes(q) ||
            j.processDesc?.toLowerCase().includes(q) ||
            j.technicianName?.toLowerCase().includes(q)
        )
    );
  }, [
    showMachineWithJobsOnly,
    machineSchedulesWithJobs,
    machineSchedules,
    debouncedSearch
  ]);

  const technicianFilterableAttributes: FilterableAttribute[] = [
    {
      id: "technicianName",
      field: "technicianName",
      label: "Technician",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "jobOrderNo",
      field: "jobOrderNo",
      label: "Job Order No",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "processName",
      field: "processName",
      label: "Process",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "machineName",
      field: "machineName",
      label: "Machine",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "status",
      field: "status",
      label: "Status",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "startTime",
      field: "startTime",
      label: "Start Time",
      dataType: AttributeDataType.DATETIME
    },
    {
      id: "endTime",
      field: "endTime",
      label: "End Time",
      dataType: AttributeDataType.DATETIME
    }
  ];

  const technicianSortableAttributes: SortableAttribute[] = [
    { id: "technicianName", field: "technicianName", label: "Technician" },
    { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
    { id: "processName", field: "processName", label: "Process" },
    { id: "machineName", field: "machineName", label: "Machine" },
    { id: "priority", field: "priority", label: "Priority" },
    { id: "status", field: "status", label: "Status" },
    { id: "startTime", field: "startTime", label: "Start Time" },
    { id: "endTime", field: "endTime", label: "End Time" }
  ];

  const machineFilterableAttributes: FilterableAttribute[] = [
    {
      id: "machineName",
      field: "machineName",
      label: "Machine",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "shopName",
      field: "shopName",
      label: "Shop",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "jobOrderNo",
      field: "jobOrderNo",
      label: "Job Order No",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "processName",
      field: "processName",
      label: "Process",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "technicianName",
      field: "technicianName",
      label: "Technician",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "status",
      field: "status",
      label: "Status",
      dataType: AttributeDataType.TEXT
    },
    {
      id: "startTime",
      field: "startTime",
      label: "Start Time",
      dataType: AttributeDataType.DATETIME
    },
    {
      id: "endTime",
      field: "endTime",
      label: "End Time",
      dataType: AttributeDataType.DATETIME
    }
  ];

  const machineSortableAttributes: SortableAttribute[] = [
    { id: "machineName", field: "machineName", label: "Machine" },
    { id: "shopName", field: "shopName", label: "Shop" },
    { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
    { id: "processName", field: "processName", label: "Process" },
    { id: "technicianName", field: "technicianName", label: "Technician" },
    { id: "priority", field: "priority", label: "Priority" },
    { id: "status", field: "status", label: "Status" },
    { id: "startTime", field: "startTime", label: "Start Time" },
    { id: "endTime", field: "endTime", label: "End Time" }
  ];

  const calendarTabs: TabItemProps[] = [
    {
      value: "job",
      label: "Job",
      icon: (
        <div className="aero:flex aero:items-center aero:gap-2">
          <Briefcase className="aero:h-4 aero:w-4" />
          <Badge variant="secondary" className="aero:text-xs">
            {scheduledJobs.length}
          </Badge>
        </div>
      ),
      content: (
        <JobGanttChart
          jobs={scheduledJobs}
          isLoading={isLoading}
          searchValue={search}
          onSearchChange={setSearch}
          filterableAttributes={filterableAttributes}
          dynamicFilters={filters}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          sortableAttributes={sortableAttributes}
          dynamicSorts={sorts}
          onAddSort={addSort}
          onUpdateSort={updateSort}
          onRemoveSort={removeSort}
          onClearSorts={clearSorts}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
      )
    },
    {
      value: "technician",
      label: "Technician",
      icon: (
        <div className="aero:flex aero:items-center aero:gap-2">
          <User className="aero:h-4 aero:w-4" />
          <Badge variant="secondary" className="aero:text-xs">
            {showTechnicianWithJobsOnly
              ? technicianSchedulesWithJobs.length
              : technicianMasterRows.length || technicianSchedules.length}
          </Badge>
        </div>
      ),
      content: (
        <TechnicianGanttChart
          technicians={visibleTechnicianSchedules}
          isLoading={isLoadingTechnicians || isLoadingTechnicianList}
          searchValue={search}
          onSearchChange={setSearch}
          filterableAttributes={technicianFilterableAttributes}
          dynamicFilters={filters}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          sortableAttributes={technicianSortableAttributes}
          dynamicSorts={sorts}
          onAddSort={addSort}
          onUpdateSort={updateSort}
          onRemoveSort={removeSort}
          onClearSorts={clearSorts}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
      )
    },
    {
      value: "machine",
      label: "Machine",
      icon: (
        <div className="aero:flex aero:items-center aero:gap-2">
          <Cog className="aero:h-4 aero:w-4" />
          <Badge variant="secondary" className="aero:text-xs">
            {showMachineWithJobsOnly
              ? machineSchedulesWithJobs.length
              : machineMasterRows.length || machineSchedules.length}
          </Badge>
        </div>
      ),
      content: (
        <MachineGanttChart
          machines={visibleMachineSchedules}
          isLoading={isLoadingMachines || isLoadingMachineList}
          searchValue={search}
          onSearchChange={setSearch}
          filterableAttributes={machineFilterableAttributes}
          dynamicFilters={filters}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          sortableAttributes={machineSortableAttributes}
          dynamicSorts={sorts}
          onAddSort={addSort}
          onUpdateSort={updateSort}
          onRemoveSort={removeSort}
          onClearSorts={clearSorts}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
      )
    }
  ];

  const handleAskAi = () => {
    if (!workspaceId) {
      return;
    }

    const candidateWorkflowIds = Array.from(
      new Set(
        pendingUnscheduledRows
          .map((row) => row.workflowId)
          .filter((id): id is number => typeof id === "number")
      )
    );
    const candidateRowsByWorkflowId = new Map(
      pendingUnscheduledRows
        .filter((row) => typeof row.workflowId === "number")
        .map((row) => [row.workflowId as number, row])
    );

    askAiPlanning.mutate(
      {
        workspaceId,
        search: planningQueryParams.search,
        filters: planningQueryParams.filters,
        sorts: planningQueryParams.sorts,
        workflowIds: candidateWorkflowIds
      } as any,
      {
        onSuccess: (result) => {
          const rawRecommendations =
            (result as any)?.recommendations ||
            (result as any)?.Recommendations ||
            [];

          const recommendations = rawRecommendations
            .map((item: any) => normalizeRecommendation(item))
            .filter(
              (
                item: JobPlanningRecommendationItem | null
              ): item is JobPlanningRecommendationItem => !!item
            )
            .flatMap((item: JobPlanningRecommendationItem) => {
              const sourceRow = candidateRowsByWorkflowId.get(item.workflowId);
              if (!sourceRow) {
                return [];
              }

              // The requested workflow is the source of truth for job identity.
              // Never trust potentially stale/mis-associated job fields in the
              // recommendation response when that workflow is known locally.
              return [{
                ...item,
                jobOrderId: sourceRow.jobOrderId,
                jobOrderNo: sourceRow.jobOrderNo || null,
                taskNo: item.taskNo || sourceRow.processName || null,
                processName: item.processName || sourceRow.processName || null,
                partNumber: item.partNumber || sourceRow.partNumber || null,
                partDescription: item.partDescription || sourceRow.partDescription || null,
                customerName: item.customerName || sourceRow.customerName || null
              }];
            });

          const recommendationWorkflowIds = new Set(
            recommendations.map(
              (item: JobPlanningRecommendationItem) => item.workflowId
            )
          );

          const infeasible = (
            (result as any)?.infeasibleWorkflows ||
            (result as any)?.infeasible_workflows ||
            (result as any)?.InfeasibleWorkflows ||
            []
          ).filter((item: JobPlanningInfeasibleItem) => {
            if (
              typeof item?.workflowId === "number" &&
              candidateWorkflowIds.includes(item.workflowId)
            ) {
              return true;
            }

            return false;
          });

          // Keep recommendations and infeasible lists disjoint.
          const filteredInfeasible = infeasible.filter(
            (item: JobPlanningInfeasibleItem) =>
              !recommendationWorkflowIds.has(item.workflowId)
          );
          setAiRecommendations(recommendations);
          setAiInfeasible(filteredInfeasible);

          toastManager.showSuccess(
            `AI planned ${recommendations.length} workflow(s)`,
            filteredInfeasible.length > 0
              ? `${filteredInfeasible.length} workflow(s) are infeasible and listed on the left panel.`
              : "All returned workflows are scheduled in preview mode."
          );
        },
        onError: (error: any) => {
          const message =
            error?.message ||
            (Array.isArray(error?.Message) ? error.Message.join("\n") : null) ||
            "Unable to generate AI planning recommendations.";

          toastManager.showError("AI planning failed", message);
        }
      }
    );
  };

  const handleSaveAiPlan = async () => {
    if (aiRecommendations.length === 0) {
      return;
    }

    type WorkflowUpdatePayload = {
      jobOrderWorkflowId: number;
      startDate: string;
      technicianId: number;
      machineId: number;
    };

    const groupedUpdates = new Map<number, WorkflowUpdatePayload[]>();

    const groupedByWorkflow = new Map<
      number,
      Map<number, WorkflowUpdatePayload>
    >();

    aiRecommendations.forEach((item) => {
      if (!item.jobOrderId || !item.workflowId) {
        return;
      }

      const byWorkflow =
        groupedByWorkflow.get(item.jobOrderId) ??
        new Map<number, WorkflowUpdatePayload>();

      byWorkflow.set(item.workflowId, {
        jobOrderWorkflowId: item.workflowId,
        startDate: item.recommendedStartDatetime || "",
        technicianId: item.recommendedTechnicianId,
        machineId: item.recommendedMachineId
      });

      groupedByWorkflow.set(item.jobOrderId, byWorkflow);
    });

    groupedByWorkflow.forEach((byWorkflow, jobOrderId) => {
      groupedUpdates.set(jobOrderId, Array.from(byWorkflow.values()));
    });

    try {
      await Promise.all(
        Array.from(groupedUpdates.entries()).map(
          ([jobOrderId, workflowUpdates]) =>
            saveAiPlanning.mutateAsync({
              jobOrderId,
              workflowUpdates
            })
        )
      );

      setAiRecommendations([]);
      setAiInfeasible([]);
      await invalidateQueries([
        ["JobSchedule"],
        ["TechnicianSchedule"],
        ["MachineSchedule"],
        ["TechnicianList"],
        ["MachineListGroupedByShop"],
      ]);
      await Promise.all([
        refetchPlanning(),
        refetchTechnician(),
        refetchMachine(),
        refetchTechnicianList(),
        refetchMachineList()
      ]);

      toastManager.showSuccess(
        "AI planning saved",
        "Recommended start date and technician/machine assignment were persisted to database."
      );
    } catch (error: any) {
      const message =
        error?.message ||
        (Array.isArray(error?.Message) ? error.Message.join("\n") : null) ||
        "Failed to save AI planning recommendations.";

      toastManager.showError("Save failed", message);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
      <DrawerContent className="aero:h-[calc(100vh-80px)] aero:max-h-screen aero:p-0">
        <DrawerHeader className="aero:border-b aero:px-6 aero:py-4">
          <div className="aero:flex aero:items-center aero:justify-between aero:gap-3">
            <div>
              <DrawerTitle>AI Planning Board</DrawerTitle>
              <p className="aero:text-sm aero:text-muted-foreground">
                Review all workflows and identify items that still need
                planning.
              </p>
              {aiRecommendations.length > 0 && (
                <p className="aero:text-xs aero:text-emerald-700 aero:mt-1">
                  Showing {aiRecommendations.length} AI recommendation(s) in
                  preview.
                </p>
              )}
            </div>
            <Button
              intent="neutral"
              design="outline"
              disabled={
                !workspaceId ||
                askAiPlanning.isPending ||
                pendingUnscheduledRows.length === 0
              }
              onClick={handleAskAi}
            >
              <Sparkles className="aero:h-4 aero:w-4" />
              {askAiPlanning.isPending ? "Planning..." : "Ask AI"}
            </Button>
            <Button
              intent="primary"
              disabled={
                saveAiPlanning.isPending || aiRecommendations.length === 0
              }
              onClick={handleSaveAiPlan}
            >
              <Save className="aero:h-4 aero:w-4" />
              {saveAiPlanning.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </DrawerHeader>

        <div className="aero:relative aero:grid aero:h-full aero:min-h-0 aero:grid-cols-[360px_minmax(0,1fr)] aero:gap-4 aero:px-6 aero:py-4">
          <Card className="aero:flex aero:min-h-0 aero:flex-col aero:overflow-hidden">
            <CardHeader className="aero:pb-3">
              <CardTitle className="aero:flex aero:items-center aero:justify-between">
                <span>Needs Planning</span>
                <div className="aero:flex aero:items-center aero:gap-2">
                  {aiInfeasible.length > 0 && (
                    <Badge variant="outline" className="aero:text-amber-700">
                      Infeasible {aiInfeasible.length}
                    </Badge>
                  )}
                  <Badge variant="secondary">
                    {pendingUnscheduledRows.length}
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="aero:flex-1 aero:min-h-0 aero:overflow-auto aero:space-y-2">
              {pendingUnscheduledRows.length === 0 ? (
                <p className="aero:text-sm aero:text-muted-foreground">
                  All workflows already have an EDD date.
                </p>
              ) : (
                pendingUnscheduledRows.map((row, index) => (
                  <div
                    key={`${row.workflowId || "wf"}-${row.jobOrderId}-${row.processName || "process"}-${index}`}
                    className="aero:rounded-md aero:border aero:p-3"
                  >
                    <div className="aero:flex aero:items-start aero:justify-between aero:gap-2">
                      <p className="aero:text-sm aero:font-semibold">
                        {row.jobOrderNo}
                      </p>
                      <Badge variant="outline">
                        {row.status || "Unscheduled"}
                      </Badge>
                    </div>
                    <p className="aero:text-xs aero:text-muted-foreground">
                      {row.partNumber || "No Part"} -{" "}
                      {row.partDescription || "No Description"}
                    </p>
                    <p className="aero:mt-1 aero:text-xs">
                      Process: {row.processName || "N/A"}
                    </p>
                    <p className="aero:text-xs aero:text-muted-foreground">
                      Technician: {row.technicianName || "Unassigned"}
                    </p>
                    <p className="aero:text-xs aero:text-muted-foreground">
                      Machine: {row.machineName || "Unassigned"}
                    </p>
                  </div>
                ))
              )}

              {aiInfeasible.length > 0 && (
                <div className="aero:pt-2 aero:border-t">
                  <p className="aero:text-xs aero:font-semibold aero:uppercase aero:text-amber-700 aero:mb-2">
                    Infeasible Workflows
                  </p>
                  <div className="aero:space-y-2">
                    {aiInfeasible.map((item) => (
                      <div
                        key={`infeasible-${item.workflowId}`}
                        className="aero:rounded-md aero:border aero:border-amber-200 aero:bg-amber-50/40 aero:p-3"
                      >
                        <div className="aero:flex aero:items-start aero:justify-between aero:gap-2">
                          <p className="aero:text-sm aero:font-semibold">
                            {item.jobOrderNo || `Job #${item.jobOrderId}`}
                          </p>
                          <Badge
                            variant="outline"
                            className="aero:text-amber-700"
                          >
                            <AlertTriangle className="aero:h-3 aero:w-3 aero:mr-1" />
                            {item.reason}
                          </Badge>
                        </div>
                        <p className="aero:text-xs aero:mt-1">
                          {item.processName || "N/A"}
                        </p>
                        <p className="aero:text-xs aero:text-muted-foreground aero:mt-1">
                          {item.message}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="aero:min-h-0 aero:overflow-x-auto aero:overflow-y-auto">
            {!workspaceId ? (
              <Card>
                <CardContent className="aero:flex aero:h-80 aero:items-center aero:justify-center">
                  <p className="aero:text-sm aero:text-muted-foreground">
                    Select a workspace to load planning data.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <TabsComponent
                  tabs={calendarTabs}
                  activeTab={activeCalendarTab}
                  setActiveTab={setActiveCalendarTab}
                  scrollable={false}
                />
              </>
            )}
          </div>

          {askAiPlanning.isPending && (
            <div className="aero:absolute aero:inset-0 aero:z-40 aero:flex aero:items-center aero:justify-center aero:bg-background/70 aero:backdrop-blur-sm">
              <div className="aero:relative aero:overflow-hidden aero:rounded-xl aero:border aero:border-border/70 aero:bg-background/95 aero:px-5 aero:py-4 aero:shadow-lg">
                <div className="aero:pointer-events-none aero:absolute aero:inset-0 aero:bg-[linear-gradient(120deg,rgba(6,182,212,0.16),rgba(59,130,246,0.20),rgba(16,185,129,0.16))] aero:bg-size-[220%_220%] aero:animate-[aiGradientFlow_18s_ease-in-out_infinite]" />
                <div className="aero:relative aero:flex aero:items-center aero:gap-2">
                  <Loader2 className="aero:h-4 aero:w-4 aero:animate-spin" />
                  <span className="aero:text-sm aero:font-medium">
                    Generating AI plan...
                  </span>
                  <span className="aero:text-xs aero:font-semibold aero:text-muted-foreground">
                    {aiPlanningProgress}%
                  </span>
                </div>
                <div className="aero:relative aero:mt-3 aero:h-1.5 aero:w-56 aero:overflow-hidden aero:rounded-full aero:bg-muted">
                  <div
                    className="aero:h-full aero:bg-linear-to-r aero:from-cyan-400 aero:via-blue-500 aero:to-emerald-400 aero:bg-size-[200%_100%] aero:animate-[aiGradientFlow_100s_linear_infinite] aero:transition-[width] aero:duration-2000"
                    style={{ width: `${aiPlanningProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          <style>
            {`@keyframes aiGradientFlow {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }`}
          </style>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
